import { AppId } from '@rockwell-automation-inc/common-utils';
import { Delayed, ResourceType, Role } from '../lemans-app.model';
export interface UserAccessState {
  userRoleHierarchy: Delayed<UserRoleHierarchy>;
}

export const initialUserAccessState: UserAccessState = {
  userRoleHierarchy: new Delayed<UserRoleHierarchy>(),
};

/**
 * Hierarchical resource node (tenant or sub-tenant)
 * Maps to api response structure for user roles hierarchy,
 */
export interface RoleNode {
  id: string;
  name: string;
  directRole: Role | null;
  effectiveRole: Role | null;
  parent: string | null;
  services: ServiceRoleAssignment[];
  children: RoleNode[];
}

export interface UserRoleHierarchy {
  email: string;
  name: string;
  roleHierarchy: RoleNode;
}

/**
 * Service node under a resource
 */
export interface ServiceRoleAssignment {
  id: string;
  kind: AppId;
  enabled: boolean;
  directRole: Role | null;
  effectiveRole: Role | null;
}

/**
 * Flattened AG-Grid tree row for resources
 * Serves dual purpose: grid row data AND OPA Rego input format
 */
export interface IRoleAssignmentRow {
  id: string;
  name: string;
  resourceType: ResourceType;
  directRole: Role | null;
  effectiveRole: Role | null;
  hierarchyPath: string[];
  /**
   * The tenant ID that contains/owns this resource
   * For services: the parent tenant that owns the service
   * For child tenants: the parent tenant that owns this tenant
   * Used in role update requests to scope the operation to the correct tenant context
   */
  tenantId: string;
  /**
   * Parent resource ID for this resource (immediate parent in hierarchy)
   * For services: the tenant ID they belong to
   * For child tenants: the parent tenant ID
   * For root tenants: null (no parent)
   * Used as input to OPA Rego policy for parent-child role inheritance evaluation
   */
  parentId: string | null;
  isSubOrg: boolean;
}

// Represents OPA evaluated user roles result with inheritance and override information.
export interface IRolesEvaluationResult {
  readonly id: string;
  readonly duplicate: boolean;
  readonly directRoleOverridden: boolean;
  readonly inheritedRoleOverridden: boolean;
  readonly modifiable: boolean;
  readonly inheritedRole: Role | null;
  readonly effectiveRole: Role;
  /**
   * Indicates if the inherited role comes from a sub-organization
   * Used to determine inherited role tooltip: "root organization" vs "sub-organization"
   */
  readonly isInheritedFromSubOrg: boolean;
}

/**
 * Represents a user role assignment row with evaluated role attributes and manageability status.
 *
 * Wraps {@link IRoleAssignmentRow} and {@link IRolesEvaluationResult} to provide
 * a unified interface for managing user role assignments in the UI.
 * Tracks whether a resource is manageable and maintains state for role update actions.
 */
export class RoleAssignmentRow implements IRoleAssignmentRow, IRolesEvaluationResult {
  private readonly _roleAssignment: IRoleAssignmentRow;
  private readonly _evaluatedRoleAttributes: IRolesEvaluationResult;
  private readonly _isManageableResource: boolean;
  private readonly _isViewingSelfRoles: boolean;
  private roleToAssign: Role | null;
  private action: UpdateAction;

  constructor(
    roleAssignment: IRoleAssignmentRow,
    evaluatedRoleAttributes: IRolesEvaluationResult,
    isManageableResource: boolean,
    isViewingSelfRoles: boolean,
  ) {
    this._roleAssignment = roleAssignment;
    this._evaluatedRoleAttributes = evaluatedRoleAttributes;
    this._isManageableResource = isManageableResource;
    this._isViewingSelfRoles = isViewingSelfRoles;
  }

  get id(): string {
    return this._roleAssignment.id;
  }
  get name(): string {
    return this._roleAssignment.name;
  }
  get resourceType(): ResourceType {
    return this._roleAssignment.resourceType;
  }
  get directRole(): Role | null {
    return this._roleAssignment.directRole;
  }
  get effectiveRole(): Role {
    return this._evaluatedRoleAttributes.effectiveRole;
  }
  get hierarchyPath(): string[] {
    return this._roleAssignment.hierarchyPath;
  }
  get tenantId(): string {
    return this._roleAssignment.tenantId;
  }
  get parentId(): string | null {
    return this._roleAssignment.parentId;
  }
  get isSubOrg(): boolean {
    return this._roleAssignment.isSubOrg;
  }
  get duplicate(): boolean {
    return this._evaluatedRoleAttributes.duplicate;
  }
  get directRoleOverridden(): boolean {
    return this._evaluatedRoleAttributes.directRoleOverridden;
  }
  get inheritedRoleOverridden(): boolean {
    return this._evaluatedRoleAttributes.inheritedRoleOverridden;
  }
  get inheritedRole(): Role | null {
    return this._evaluatedRoleAttributes.inheritedRole;
  }
  get isInheritedFromSubOrg(): boolean {
    return this._evaluatedRoleAttributes.isInheritedFromSubOrg;
  }
  get isManageable(): boolean {
    return this._isManageableResource;
  }
  get enabled(): boolean {
    // If the user is viewing their own roles
    // we enable the row for display purposes, but disable role modification
    if (this._isViewingSelfRoles) {
      return true;
    }
    return this.modifiable;
  }
  get modifiable(): boolean {
    // cannot modify own roles if viewing self roles
    if (this._isViewingSelfRoles) {
      return false;
    }
    return this._evaluatedRoleAttributes.modifiable && this._isManageableResource;
  }
  get isRoleTransferable(): boolean {
    return this._isViewingSelfRoles && this.directRole === Role.Owner;
  }
  get rowAction(): { roleToAssign: Role | null; action: UpdateAction } {
    return { roleToAssign: this.roleToAssign, action: this.action };
  }

  setRoleToAssign(roleToAssign: Role): void {
    this.roleToAssign = roleToAssign;
    this.action = 'set';
  }

  removeRoleAssignment(): void {
    this.roleToAssign = null;
    this.action = 'remove';
  }

  getRow(): IRoleAssignmentRow {
    if (this.rowAction.action === 'set' || this.action === 'remove') {
      return { ...this._roleAssignment, directRole: this.rowAction.roleToAssign };
    } else {
      return this._roleAssignment;
    }
  }
}

export type UpdateAction = 'set' | 'remove';

export type RoleUpdateRequest = {
  tenantId: string;
  resourceId: string;
  role: Role | null;
  action: UpdateAction;
};

export type RoleUpdateOutcome = {
  eventsEmitted: number | null;
  error: string | null;
  isSuccess: boolean;
};

export type RoleUpdateResponse = {
  errorCount: number;
  outcome: Record<string, RoleUpdateOutcome>;
};
